import React, { useRef } from "react";
import { HIGHLIGHT_DATA, HIGHLIGHTS_CHANNEL_URL } from "../data/highlights";
import { useTranslationsById } from "../../hooks/use-translations-by-id";
import useIsMobile from "../../js/utilities/useIsMobile";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Mousewheel, Navigation } from "swiper";
import { Icon } from "@iconify/react";

/**
 * Sección de highlights audiovisuales de Reading Resources.
 *
 * Presenta contenidos destacados y enlaza al canal completo.
 */
const PlayIcon = () => (
	<svg width="15" height="18" viewBox="0 0 15 18" fill="none" xmlns="http://www.w3.org/2000/svg">
		<path
			d="M1 1V17L14 9L1 1Z"
			stroke="white"
			stroke-width="2"
			stroke-linecap="round"
			stroke-linejoin="round"
		/>
	</svg>
);

const ArrowRightIcon = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 18 18"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true"
		focusable="false"
	>
		<path
			d="M6 4L11 9L6 14"
			stroke="currentColor"
			strokeWidth="2"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

/**
 * Sección de videos destacados para recursos de lectura.
 *
 * @component
 * @returns {JSX.Element} Grilla de highlights con CTA al canal de YouTube.
 */
export const Highlights = () => {
	const texts = useTranslationsById({ mainSectionName: "reading_resources" });
	const isMobile = useIsMobile();
	const prevButtonRef = useRef(null);
	const nextButtonRef = useRef(null);

	const updateNavigation = (swiper) => {
		swiper.params.navigation.prevEl = prevButtonRef?.current;
		swiper.params.navigation.nextEl = nextButtonRef?.current;
		swiper.navigation.destroy();
		swiper.navigation.init();
		swiper.navigation.update();
	};

	return (
		<section className="section highlights-section">
			<div className="container">
				<div className="highlights-section-heading">
					<h2 className="highlights-section-title body-base">
						{texts.getText({
							id: "highlights.title",
							defaultText: "HIGHLIGHTS",
						})}
					</h2>
				</div>

				<div className="d-flex d-lg-none justify-content-end my-4">
					<button
						ref={prevButtonRef}
						aria-label={texts.getText({
							id: "highlights.button.previous.aria",
							defaultText: "Previous highlights",
						})}
						className="home-swiper-navigation-buttons mx-2"
					>
						<Icon
							className="icon"
							icon="material-symbols:arrow-back-rounded"
							width={20}
						/>
					</button>
					<button
						ref={nextButtonRef}
						aria-label={texts.getText({
							id: "highlights.button.next.aria",
							defaultText: "Next highlights",
						})}
						className="home-swiper-navigation-buttons"
					>
						<Icon
							className="icon"
							icon="material-symbols:arrow-forward-rounded"
							width={20}
						/>
					</button>
				</div>

				{isMobile ? (
					<Swiper
						style={{ overflowX: "hidden" }}
						slidesPerGroup={1}
						slidesPerView={1}
						spaceBetween={24}
						breakpoints={{
							670: {
								slidesPerView: 2,
								slidesPerGroup: 2,
							},
						}}
						autoplay={{
							delay: 5000,
							disableOnInteraction: false,
						}}
						mousewheel={{
							forceToAxis: true,
						}}
						navigation={{
							nextEl: nextButtonRef?.current,
							prevEl: prevButtonRef?.current,
						}}
						onSwiper={(swiper) => {
							updateNavigation(swiper);
						}}
						modules={[Autoplay, Navigation, Mousewheel]}
					>
						{HIGHLIGHT_DATA.map((item) => (
							<SwiperSlide
								key={item.id}
								className="px-1 d-flex justify-content-center align-items-center"
							>
								<a
									className="highlights-card"
									href={item.videoUrl}
									target="_blank"
									rel="noopener noreferrer"
									title={texts.getText({
										id: item.title.translationPath,
										defaultText: item.title.default,
									})}
								>
									<div className="highlights-card-media">
										<img
											className="highlights-card-image"
											src={item.image}
											alt=""
											aria-hidden="true"
										/>
									</div>
									<div className="highlights-card-content">
										<p className="highlights-card-title">
											{texts.getText({
												id: item.title.translationPath,
												defaultText: item.title.default,
											})}
										</p>
										<span className="highlights-card-play d-flex justify-content-center align-items-center">
											<PlayIcon />
										</span>
									</div>
								</a>
							</SwiperSlide>
						))}
					</Swiper>
				) : (
					<div className="row m-0 highlights-section-grid">
						{HIGHLIGHT_DATA.map((item) => (
							<div
								key={item.id}
								className="col-12 col-md-6 col-xl-3 p-3 d-flex justify-content-center"
							>
								<a
									className="highlights-card"
									href={item.videoUrl}
									target="_blank"
									rel="noopener noreferrer"
									title={texts.getText({
										id: item.title.translationPath,
										defaultText: item.title.default,
									})}
								>
									<div className="highlights-card-media">
										<img
											className="highlights-card-image"
											src={item.image}
											alt=""
											aria-hidden="true"
										/>
									</div>
									<div className="highlights-card-content">
										<p className="highlights-card-title">
											{texts.getText({
												id: item.title.translationPath,
												defaultText: item.title.default,
											})}
										</p>
										<span className="highlights-card-play d-flex justify-content-center align-items-center">
											<PlayIcon />
										</span>
									</div>
								</a>
							</div>
						))}
					</div>
				)}

				<div className="highlights-section-actions">
					<a
						className="buttonv2 button-secondary focusable-red"
						href={HIGHLIGHTS_CHANNEL_URL}
						target="_blank"
						rel="noopener noreferrer"
						aria-label={texts.getText({
							id: "highlights.button.youtube.aria",
							defaultText: "Open MakeMake YouTube channel",
						})}
					>
						<span>
							{texts.getText({
								id: "highlights.button.youtube",
								defaultText: "Subscribe to Youtube channel",
							})}
						</span>
						<ArrowRightIcon />
					</a>
				</div>
			</div>
		</section>
	);
};
